import cn from "classnames"
import React from "react"
import { useTranslation } from "react-i18next"
import Scrollable from "@/common/components/scrollable"
import AutoGrid from "@/signomatic/components/auto_grid"
import Hint from "@/signomatic/components/hint"
import ColorUtil, { type ColorSet } from "@/signomatic/util/colors"
import { EngravingIcon } from "@/skylticons"

interface FixedColorPickerProps<T> {
  colors: T[]
  current?: T
  customPicker: React.ReactNode
  mobile?: boolean
  insideNote?: boolean
  disabledColors?: T[]
  adminColors?: T[]
  onChange: (colors: T) => void
  signType?: string
}

const inside = (set: ColorSet | string) => {
  if (ColorUtil.isSet(set)) {
    return ["#daa520", "#bbbbbb", "#c27268"].includes(set[0])
  }

  return false
}

const FixedColorPicker = <T extends ColorSet | string>({
  colors,
  current,
  insideNote,
  disabledColors,
  adminColors,
  onChange,
  signType,
}: FixedColorPickerProps<T>) => {
  const { t } = useTranslation()
  const hintTranslation =
    signType === "thermal_transfer_label" ? "toolbox.color_disabled_size" : "toolbox.color_disabled_thickness"

  const onClick = (set: T) => {
    const showHint = isDisabledColor(set)
    const hint = document.getElementsByClassName("hint-outer")[0]

    if (!hint) {
      return
    }

    if (showHint) {
      hint.classList.remove("hidden")
    } else {
      onChange(set)
      hint.classList.add("hidden")
    }
  }

  const isDisabledColor = (set: T) => {
    if (!disabledColors) {
      return false
    }
    return ColorUtil.isDisabled(disabledColors as ColorSet[], set)
  }

  return (
    <Scrollable>
      <div className="color-picker fixed-color-picker">
        <AutoGrid>
          {colors.map(set => (
            <div
              key={set.toString()}
              className={cn("color-button", ColorUtil.colorClass(set), `color-${ColorUtil.colorClass(set)}`, {
                active: ColorUtil.sameColor(set, current),
                disabled: isDisabledColor(set),
              })}
              onClick={() => onClick(set)}
            >
              <div className="color-wrapper">
                <div className="color-background"></div>
                <div
                  className="color"
                  style={{
                    backgroundColor: ColorUtil.backgroundColor(set),
                    backgroundImage: ColorUtil.backgroundImage(set),
                  }}
                >
                  {ColorUtil.isSet(set) && (
                    <div className="icon" style={{ color: ColorUtil.textColor(set) }}>
                      <EngravingIcon />
                    </div>
                  )}
                </div>
              </div>
              <div className="name">
                <p className="paragraph-2xs center mb-0">
                  {ColorUtil.colorClass(set).includes("stainless")
                    ? t(`misc.textfarg`)
                    : t(`background_colors.${ColorUtil.isSet(set) ? set[0] : set}`)}
                </p>
                {ColorUtil.isSet(set) && <p className="paragraph-2xs center mb-0">{t(`content_colors.${set[1]}`)}</p>}
                {inside(set) && insideNote && <p className="paragraph-2xs center mb-0">({t("misc.not_outside")})</p>}
              </div>
            </div>
          ))}
        </AutoGrid>
        {(adminColors?.length || 0) > 0 && (
          <div>
            <div className="border-t-4 border-t-ink-85 pt-1">SignAdmin users only</div>
            <AutoGrid>
              {adminColors?.map(set => (
                <div
                  key={set.toString()}
                  className={cn("color-button", ColorUtil.colorClass(set), `color-${ColorUtil.colorClass(set)}`, {
                    active: ColorUtil.sameColor(set, current),
                    disabled: isDisabledColor(set),
                  })}
                  onClick={() => onClick(set)}
                >
                  <div className="color-wrapper">
                    <div className="color-background"></div>
                    <div
                      className="color"
                      style={{
                        backgroundColor: ColorUtil.backgroundColor(set),
                        backgroundImage: ColorUtil.backgroundImage(set),
                      }}
                    >
                      {ColorUtil.isSet(set) && (
                        <div className="icon" style={{ color: ColorUtil.textColor(set) }}>
                          <EngravingIcon />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="name">
                    <p className="paragraph-2xs center mb-0">
                      {t(`background_colors.${ColorUtil.isSet(set) ? set[0] : set}`)}
                    </p>
                    {ColorUtil.isSet(set) && (
                      <p className="paragraph-2xs center mb-0">{t(`content_colors.${set[1]}`)}</p>
                    )}
                    {inside(set) && insideNote && (
                      <p className="paragraph-2xs center mb-0">({t("misc.not_outside")})</p>
                    )}
                  </div>
                </div>
              ))}
            </AutoGrid>
          </div>
        )}

        <div className="hint-outer hidden">
          <Hint show type="errormessage" translation={hintTranslation} />
        </div>
      </div>
    </Scrollable>
  )
}

export default FixedColorPicker
